import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Heading,
  VStack,
  Alert,
  AlertIcon,
  FormControl,
  FormLabel,
  Input,
  useColorModeValue,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Stack,
  Divider,
} from '@chakra-ui/react';
import { redeemCoupon } from '../services/CompanyService';

const CompanyAccess = () => {
  const [zipCode, setZipCode] = useState('');
  const [promotionCode, setPromotionCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [redemptionData, setRedemptionData] = useState<any>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const formatPromotionCode = (value: string) => {
    const digits = value.replace(/\D/g, '');
    const limitedDigits = digits.slice(0, 6);
    
    if (limitedDigits.length >= 4) {
      return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3)}`;
    }
    
    return limitedDigits;
  };

  const formatZipCode = (value: string) => {
    return value.replace(/\D/g, '').slice(0, 5);
  };

  const formatCouponCodeDisplay = (code: string) => {
    if (!code || code.length !== 6) return code;
    return `${code.slice(0, 3)}-${code.slice(3)}`;
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (!zipCode || !promotionCode) {
      setError('Please enter both zip code and promotion code');
      return;
    }

    if (zipCode.length !== 5) {
      setError('Please enter a valid 5-digit zip code');
      return;
    }

    const cleanPromotionCode = promotionCode.replace(/\D/g, '');
    if (cleanPromotionCode.length !== 6) {
      setError('Please enter a valid 6-digit promotion code');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      const result = await redeemCoupon({
        couponCode: cleanPromotionCode,
        zipCode: zipCode
      });

      if (result.success) {
        setRedemptionData(result.coupon);
        onOpen();
        // Clear form after successful redemption
        setZipCode('');
        setPromotionCode('');
      } else {
        setError(result.error || 'Failed to redeem promotion');
      }
      
    } catch (err: any) {
      setError(err.message || 'Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxW="md" py={10}>
      <Box
        bg={bgColor}
        p={6}
        borderRadius="md"
        boxShadow="md"
        border="1px"
        borderColor={borderColor}
      >
        <VStack spacing={6} align="stretch">
          <Box textAlign="center">
            <Heading size="lg" color="mamaFuel.moss.700">
              Company Access
            </Heading>
          </Box>
          
          <Box textAlign="center" px={2}>
            <Text fontSize="md" color="gray.600">
              Redeem customer promotion codes by entering your store's zip code and the customer's promotion code below.
            </Text>
          </Box>
          
          <FormControl isRequired>
            <FormLabel>Store Zip Code</FormLabel>
            <Input
              type="text"
              value={zipCode}
              onChange={(e) => setZipCode(formatZipCode(e.target.value))}
              onFocus={() => setError('')}
              maxLength={5}
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Customer Promotion Code</FormLabel>
            <Input
              type="text"
              value={promotionCode}
              onChange={(e) => setPromotionCode(formatPromotionCode(e.target.value))}
              onFocus={() => setError('')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit();
                }
              }}
              maxLength={7}
            />
          </FormControl>

          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}

          <Button
            bg="mamaFuel.moss.700"
            color="white"
            _hover={{ bg: "mamaFuel.moss.600" }}
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText="Redeeming promotion..."
            disabled={isSubmitting}
          >
            Redeem Promotion
          </Button>

          <Box textAlign="center">
            <Link to="/">
              <Button variant="outline" colorScheme="blue">
                Return Home
              </Button>
            </Link>
          </Box>
        </VStack>
      </Box>

      {/* Success Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="green.600">
            ✅ Promotion Redeemed Successfully
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {redemptionData && (
              <Stack spacing={4}>
                <Box>
                  <Text fontSize="lg" fontWeight="bold" mb={2}>
                    Restaurant: {redemptionData.restaurantName}
                  </Text>
                </Box>
                
                <Divider />
                
                <Box>
                  <Text fontSize="md" fontWeight="semibold" mb={2}>
                    Promotion Available:
                  </Text>
                  {redemptionData.promotions && redemptionData.promotions.length > 0 ? (
                    <VStack align="start" spacing={1}>
                      {redemptionData.promotions.map((promotion: string, index: number) => (
                        <Text key={index} fontSize="sm" color="gray.700">
                          {promotion}
                        </Text>
                      ))}
                    </VStack>
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      No specific promotions listed
                    </Text>
                  )}
                </Box>
                
                <Divider />
                
                <Box>
                  <Text fontSize="sm" color="gray.500">
                    Coupon Code: {formatCouponCodeDisplay(redemptionData.couponCode)}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Redeemed: {new Date(redemptionData.usedAt).toLocaleString()}
                  </Text>
                </Box>
              </Stack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="green" onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default CompanyAccess;
